import { faker } from '@faker-js/faker';
import { IPiNxtIdToken } from 'types/pinxt';
import { toBase64, fromBase64 } from 'utils/generalUtils';

interface ITokenPayloadOptions {
  user_guid?: string;
  username?: string;
  email?: string;
}
/**
 * Generates a Mock Token Payload object to use within the IdToken JWT.
 */
const genTokenPayload = (options?: ITokenPayloadOptions): IPiNxtIdToken => {
  const email = options?.email ?? faker.internet.email();
  return {
    acct_num: [faker.string.uuid().toString()],
    entitlements: [],
    sub: email,
    orderId: faker.string.uuid(),
    usecase: 'portals',
    role: 'CHILD',
    cpni_compliance: 'COMPLIANT',
    identity_status: 'ACTIVE',
    spa_client: false,
    iss: 'id_token-entpor-jws-issuer',
    acct_type: ['COMMERCIAL'],
    billing_acct: [faker.string.uuid()],
    user_guid: faker.string.uuid(),
    billing_division: ['NATEE'],
    client_id: 'enterprise-portal',
    entpor_acct_guid: faker.string.uuid(),
    identity_role: 'STANDARD',
    lname: 'Mock',
    authn_source_port: '4827',
    acct_status: ['ACTIVE'],
    grant_type: 'auth_code',
    trusted_client: false,
    auth_reporting_id: faker.string.uuid(),
    auth_time: new Date().valueOf().toString(),
    billing_slice: ['182'],
    exp: new Date().valueOf(),
    iat: new Date().valueOf(),
    acct_guid: [faker.string.uuid().toString()],
    jti: faker.string.uuid(),
    email,
    auth_dla: false,
    fname: 'User',
    email_verified: false,
    device_id: faker.string.uuid(),
    'legacy-entpor-token':
      'eyJhbGciOiJ1NiJ9.eyJzdWIiOiWNmIn0.naVomKvOEL_M_lqTSIzHJglrk9dlKwngOkM2YI0dero',
    secpol: 'id_token-entpor-jws',
    identity_guid: faker.string.uuid(),
    internal_user: false,
    acct_zip: ['null'],
    aud: 'enterprise-portal',
    auth_method: 'Password',
    nbf: new Date().valueOf(),
    domain: 'enterprise',
    authn_traffic_origin_id: faker.internet.ip(),
    namespace: 'TWC',
    subject_discrim: 'IDENTITY',
    phone_number: faker.phone.number(),
    entpor_acct_status: ['Active'],
    username: email,
    ...options,
  };
};

/**
 * Generates a Mock JWT token.
 */
export const genJwtToken = (options?: ITokenPayloadOptions): string => {
  const tokenPayload = genTokenPayload(options);
  const tokenPayloadJsonStr = JSON.stringify(tokenPayload);
  const encTokenPayload = toBase64(tokenPayloadJsonStr);

  // Note: the HEADER (first part) and SIGNATURE (last part) are not valid
  return `mock_eyJza3NpZCI6ImlkX3.${encTokenPayload}.eojTAh6MjHm1yhYbnUJ18REEHZi`;
};

/**
 * Decodes the Mock JWT Token and returns the payload object
 * @param jwtToken Mock generated token
 * @returns token payload object
 */
export const decodeJwtToken = (jwtToken: string): IPiNxtIdToken => {
  const tokenPayload = jwtToken.split('.')[1];
  const tokenPayloadJsonStr = fromBase64(tokenPayload);
  return JSON.parse(tokenPayloadJsonStr);
};

/**
 * Encodes the User's Username and userId so it can be passed to the next call.
 * @param userId Current user's Id
 * @param userName Current user's username
 * @returns Encoded, base64 string
 */
export const encodeAuthCode = (username: string, userId: string): string => {
  const userJsonStr = JSON.stringify({ username, userId });
  return toBase64(userJsonStr);
};

/**
 * Decodes the auth code to extract User's Username and userId.
 * @param authCode Encoded, base64 string
 * @returns Decoded, base64 string
 */
export const decodeAuthCode = (
  authCode: string
): {
  userId: string;
  username: string;
} => {
  const cleanAuthCode = authCode.replace('CODE.', '');
  const userJsonStr = fromBase64(cleanAuthCode);
  return JSON.parse(userJsonStr);
};
